import {
  useActions,
  useFormFields,
  useFormValidator,
  useUpdateStatusEffect,
} from 'hooks';
import useRoute from 'hooks/useRoute';
import { capitalize } from 'lib/helpers';
import marketLabels from 'marketLabels';
import {
  Divider,
  makeDefaultBreakRuleFields,
  Spacing,
  SettingsSaveBtn,
} from 'page/Settings/components';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { privateRoutes } from 'routes';
import {
  BOX_ACCOUNT_ROSTERED_SHIFT_SETTINGS_UPDATE,
  getCustomBreaksStatus,
  getDefaultBreaksStatus,
  getRosteredShiftSettings,
} from 'state/Account';
import {
  BOX_SETTINGS_LAYOUT_ERRORS_CLEARED,
  BOX_SETTINGS_LAYOUT_PAGE_CLOSED,
  getPageErrors,
  getPageLoading,
  getPageStatus,
} from 'state/SettingsLayout';
import { Layout, Form, AlertErrorBoxLayout } from 'element';
import DefaultBreaksFieldGroup from './DefaultBreaksFieldGroup';
import makeFormFields from './makeFormFields';
import makePayload from './makePayload';
import OtherRosterSettingsFieldGroup from './OtherRosterSettingsFieldGroup';
import ShiftOffersFieldGroup from './ShiftOffersFieldGroup';
import ShiftSwapsFieldGroup from './ShiftSwapsFieldGroup';
import validate from './validate';
import { RosterSettingsHeader } from './components';

export default function RosterSettings() {
  const settingsUpdate = useActions(
    BOX_ACCOUNT_ROSTERED_SHIFT_SETTINGS_UPDATE.request
  );

  const rosteredShiftSettings = useSelector(getRosteredShiftSettings);
  const route = useRoute();
  const [pageClosed, errorsCleared] = useActions([
    BOX_SETTINGS_LAYOUT_PAGE_CLOSED,
    BOX_SETTINGS_LAYOUT_ERRORS_CLEARED,
  ]);
  const errors = useSelector(getPageErrors);
  const loading = useSelector(getPageLoading);

  const { formFields, setFormFields } = useFormFields(() =>
    makeFormFields(rosteredShiftSettings)
  );

  const { formErrors, submitForm } = useFormValidator(formFields, {
    validate,
    makePayload,
    onSubmit: settingsUpdate,
  });

  React.useEffect(
    () => () => {
      pageClosed();
    },
    [pageClosed]
  );

  useUpdateStatusEffect(getDefaultBreaksStatus, {
    success: () => {
      setFormFields({
        default_breaks_rules: makeDefaultBreakRuleFields(
          rosteredShiftSettings.default_breaks_rules
        ),
      });
    },
  });

  useUpdateStatusEffect(getCustomBreaksStatus, {
    success: () => {
      setFormFields({
        default_breaks_rules: makeDefaultBreakRuleFields(
          rosteredShiftSettings.default_breaks_rules
        ),
      });
    },
  });

  useUpdateStatusEffect(getPageStatus, {
    success: () => {
      route.goTo(privateRoutes.settings.path);
    },
  });

  const handleErrorsClose = () => {
    errorsCleared();
  };

  return (
    <>
      <Layout.Header>
        <RosterSettingsHeader activeTab="general" />
      </Layout.Header>
      <Layout.Content>
        <Form onSubmit={submitForm}>
          <AlertErrorBoxLayout
            BoxProps={{
              mb: 2,
            }}
            errors={errors}
            onClose={handleErrorsClose}
          />
          
          <DefaultBreaksFieldGroup
            values={formFields}
            onChange={setFormFields}
            errors={formErrors}
          />

          <Divider />

          <ShiftSwapsFieldGroup
            values={formFields}
            onChange={setFormFields}
            errors={formErrors}
          />

          <Divider />

          <ShiftOffersFieldGroup
            values={formFields}
            onChange={setFormFields}
            errors={formErrors}
          />

          <Divider />

          <OtherRosterSettingsFieldGroup
            values={formFields}
            onChange={setFormFields}
            errors={formErrors}
          />

          <SettingsSaveBtn loading={loading} />
        </Form>
      </Layout.Content>
    </>
  );
}
